import { getLegacyBridge } from "./state";

const { els } = getLegacyBridge();
let currentUser: any = null;
type AccountTab = "overview" | "credits" | "affiliate" | "settings";
let activeAccountTab: AccountTab = "overview";
let loginRequestInFlight = false;

const escapeHtml = (value: unknown): string => String(value ?? "").replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" })[char] || char);
const credit = (value: unknown): string => Number(value || 0).toLocaleString("vi-VN");
const money = (value: unknown): string => `${Number(value || 0).toLocaleString("vi-VN")}đ`;
const bytes = (value: unknown): string => {
  const amount = Math.max(0, Number(value || 0));
  if (amount >= 1024 * 1024) return `${(amount / (1024 * 1024)).toLocaleString("vi-VN", { maximumFractionDigits: 1 })} MB`;
  return `${Math.ceil(amount / 1024).toLocaleString("vi-VN")} KB`;
};
const renderLevels = [
  { id: "low", label: "Nhanh" },
  { id: "medium", label: "Cân bằng" },
  { id: "high", label: "Cao" },
];
const sizeLevels = [
  { id: "standard", label: "1K" },
  { id: "2k", label: "2K" },
  { id: "4k", label: "4K" },
];
const FALLBACK_GENERATE_COSTS: Record<string, Record<string, number>> = {
  standard: { low: 3, medium: 5, high: 10 },
  "2k": { low: 8, medium: 13, high: 25 },
  "4k": { low: 15, medium: 25, high: 50 },
};
const BALANCED_1K_COST = 5;
const DAILY_CREDIT_WELCOME_KEY = "agentra.dailyCreditWelcomeDate";
let cachedCreditPolicy: any = null;
let dailyCreditWelcomeScheduled = false;
let cachedCreditPackages: any[] = [];
let pendingOrderPollId = 0;
let pendingOrderCode = "";
let referralStatusTimerId = 0;
let referralStatusRequestId = 0;

function costMatrixSummary(costs: any): string {
  return sizeLevels.map((size) => {
    const row = costs?.[size.id] || {};
    const values = renderLevels.map((level) => `${level.label} ${credit(row[level.id])}`).join(" · ");
    return `${size.label}: ${values}`;
  }).join(" | ");
}

function packageEstimateSummary(item: any): string {
  const total = Number(item.total_credits || item.credits || 0);
  const estimates = item.image_estimates || {};
  const fast1k = estimates.fast_1k ?? estimates.low ?? Math.floor(total / 3);
  const balanced1k = estimates.balanced_1k ?? estimates.medium ?? Math.floor(total / 5);
  return `≈ ${credit(fast1k)} ảnh Nhanh 1K · ${credit(balanced1k)} ảnh Cân bằng 1K · credit không hết hạn`;
}

function setModalOpen(open: boolean): void {
  els.accountModal?.classList.toggle("hidden", !open);
  els.accountModal?.setAttribute("aria-hidden", open ? "false" : "true");
}

function setDailyCreditWelcomeOpen(open: boolean): void {
  els.dailyCreditWelcome?.classList.toggle("hidden", !open);
  els.dailyCreditWelcome?.setAttribute("aria-hidden", open ? "false" : "true");
}

function localDateKey(): string {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function shouldShowDailyCreditWelcome(): boolean {
  if (!els.dailyCreditWelcome) return false;
  try {
    return window.localStorage.getItem(DAILY_CREDIT_WELCOME_KEY) !== localDateKey();
  } catch {
    return true;
  }
}

function markDailyCreditWelcomeShown(): void {
  try {
    window.localStorage.setItem(DAILY_CREDIT_WELCOME_KEY, localDateKey());
  } catch {
    // Private browsing or storage restrictions should not block the app.
  }
}

function closeDailyCreditWelcome(): void {
  setDailyCreditWelcomeOpen(false);
  markDailyCreditWelcomeShown();
}

function scheduleDailyCreditWelcome(): void {
  if (dailyCreditWelcomeScheduled || !shouldShowDailyCreditWelcome()) return;
  dailyCreditWelcomeScheduled = true;
  window.setTimeout(() => {
    if (!shouldShowDailyCreditWelcome()) return;
    setDailyCreditWelcomeOpen(true);
    markDailyCreditWelcomeShown();
  }, 700);
}

function setMessage(message: string, type = ""): void {
  if (!els.accountStatus) return;
  els.accountStatus.textContent = message;
  els.accountStatus.className = `account-status ${type}`.trim();
}

function setReferralStatus(message: string, type = ""): void {
  if (!els.accountRegisterReferralStatus) return;
  els.accountRegisterReferralStatus.textContent = message;
  els.accountRegisterReferralStatus.className = `account-referral-status ${type}`.trim();
}

function cleanAccountTab(value: unknown): AccountTab {
  const text = String(value || "").trim();
  return text === "credits" || text === "affiliate" || text === "settings" ? text : "overview";
}

function setAccountTab(tab: AccountTab): void {
  activeAccountTab = tab;
  (els.accountProfilePanel?.querySelectorAll("[data-account-tab]") || []).forEach((button: Element) => {
    const tabButton = button as HTMLElement;
    const active = tabButton.dataset.accountTab === tab;
    tabButton.classList.toggle("active", active);
    tabButton.setAttribute("aria-selected", active ? "true" : "false");
  });
  (els.accountProfilePanel?.querySelectorAll("[data-account-tab-panel]") || []).forEach((panel: Element) => {
    const tabPanel = panel as HTMLElement;
    tabPanel.classList.toggle("hidden", tabPanel.dataset.accountTabPanel !== tab);
  });
}

function clearPendingOrderPoll(): void {
  if (pendingOrderPollId) {
    window.clearInterval(pendingOrderPollId);
    pendingOrderPollId = 0;
  }
  pendingOrderCode = "";
}

function refreshTasksAfterAccountReady(): void {
  window.setTimeout(() => {
    const method = getLegacyBridge().methods.refreshTasks;
    if (typeof method !== "function") return;
    void method({ migrateLegacyArchives: true }).catch((error: unknown) => console.warn(error));
  }, 0);
}

function renderLabel(value: string): string {
  return renderLevels.find((item) => item.id === value)?.label || "Cân bằng";
}

function sizeLabel(value: string): string {
  return sizeLevels.find((item) => item.id === value)?.label || "1K";
}

function selectedResolution(): string {
  const value = String(els.resolution?.value || "standard").trim().toLowerCase();
  return value === "2k" || value === "4k" ? value : "standard";
}

function selectedQuality(): string {
  const value = String(els.quality?.value || "medium").trim().toLowerCase();
  return value === "low" || value === "high" ? value : "medium";
}

function selectedCreditMode(): string {
  const value = String(els.creditMode?.value || "free_watermark").trim().toLowerCase();
  return value === "paid_clean" ? "paid_clean" : "free_watermark";
}

function selectedCount(): number {
  const value = Number(els.nInput?.value || 1);
  return Math.min(Math.max(Number.isFinite(value) ? value : 1, 1), 4);
}

function currentGenerateCost(): number {
  const resolution = selectedResolution();
  const quality = selectedQuality();
  const base = Number(cachedCreditPolicy?.costs?.generate?.[resolution]?.[quality] ?? FALLBACK_GENERATE_COSTS[resolution]?.[quality] ?? 5);
  return Math.max(1, base * selectedCount());
}

function remainingDailyImageCount(dailyCredit: number, cost: number): number {
  return Math.max(0, Math.floor(Math.max(0, dailyCredit) / Math.max(1, cost)));
}

function openCreditPurchase(): void {
  setDailyCreditWelcomeOpen(false);
  setModalOpen(true);
  setAccountTab("credits");
  setMessage("Credit mua không hết hạn và giúp bạn tiếp tục tạo ảnh ngay trong ngày.", "ok");
  void loadPackages();
  window.setTimeout(() => els.creditPackageList?.scrollIntoView?.({ behavior: "smooth", block: "center" }), 80);
}

function referralCodeFromUrl(): string {
  return String(new URLSearchParams(window.location.search).get("ref") || "").trim().toUpperCase();
}

function referralLink(code: string): string {
  const url = new URL(window.location.origin || "https://taoanh.online");
  url.searchParams.set("ref", code);
  return url.toString();
}

async function copyText(value: string, successMessage: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
    setAffiliateStatus(successMessage, "ok");
  } catch {
    setAffiliateStatus("Không copy được, hãy sao chép thủ công.", "error");
  }
}

function useDailyCreditNow(): void {
  closeDailyCreditWelcome();
  const prompt = els.prompt as HTMLTextAreaElement | null;
  prompt?.focus?.();
  prompt?.scrollIntoView?.({ behavior: "smooth", block: "center" });
}

function updateCreditCostHint(): void {
  const wallet = currentUser?.wallet || {};
  const dailyCredit = Number(wallet.daily ?? 100);
  const purchasedCredit = Number(wallet.purchased || 0);
  const cost = currentGenerateCost();
  const creditMode = selectedCreditMode();
  const isPaidClean = creditMode === "paid_clean";
  const activeBalance = isPaidClean ? purchasedCredit : dailyCredit;
  const remaining = remainingDailyImageCount(activeBalance, cost);
  const quality = renderLabel(selectedQuality());
  const resolution = sizeLabel(selectedResolution());
  const count = selectedCount();
  const costText = count > 1 ? `${credit(cost)} credit cho ${count} ảnh` : `${credit(cost)} credit`;
  if (els.creditModeHint) {
    els.creditModeHint.textContent = isPaidClean
      ? "Dùng credit đã mua, ảnh sạch không watermark."
      : "Dùng credit hôm nay, ảnh có watermark taoanh.online.";
  }
  if (els.creditCostHint) {
    els.creditCostHint.textContent = isPaidClean
      ? `Ảnh sạch tốn khoảng ${costText} · còn khoảng ${credit(remaining)} ảnh ${quality} ${resolution} từ credit đã mua`
      : `Miễn phí tốn khoảng ${costText} · còn khoảng ${credit(remaining)} ảnh ${quality} ${resolution} hôm nay · ảnh có watermark taoanh.online`;
  }
  if (els.dailyCreditBanner) {
    const lowDaily = currentUser && (isPaidClean ? purchasedCredit < cost : dailyCredit < 20);
    const hasPurchased = purchasedCredit > 0;
    els.dailyCreditBanner.classList.toggle("is-low-credit", Boolean(lowDaily));
    const strong = els.dailyCreditBanner.querySelector("strong");
    const copy = els.dailyCreditBanner.querySelector("span");
    if (strong) strong.textContent = isPaidClean
      ? (lowDaily ? "Sắp thiếu credit đã mua" : "Ảnh sạch dùng credit đã mua")
      : (lowDaily ? "Sắp hết credit miễn phí hôm nay" : "Tặng 100 credit mỗi ngày");
    if (copy) {
      copy.textContent = isPaidClean
        ? (lowDaily ? "Nạp thêm để tạo ảnh sạch không watermark ngay." : "Credit đã mua không hết hạn, ảnh xuất ra sạch để dùng lâu dài.")
        : (lowDaily
          ? (hasPurchased ? "Chọn Ảnh sạch để dùng credit đã mua, hoặc nạp thêm nếu muốn tạo nhiều hơn." : "Nạp credit để tạo ảnh sạch ngay, không cần chờ đến 0h.")
          : "Miễn phí mỗi ngày giúp bạn thử ý tưởng; ảnh sạch không watermark dùng credit đã mua.");
    }
  }
}

function render(): void {
  const loggedIn = Boolean(currentUser);
  scheduleDailyCreditWelcome();
  els.accountButton?.classList.toggle("active", loggedIn);
  els.mobileAuthPrompt?.classList.toggle("hidden", loggedIn);
  els.mobileAuthPrompt?.setAttribute("aria-hidden", loggedIn ? "true" : "false");
  if (els.accountButtonLabel) els.accountButtonLabel.textContent = loggedIn ? (currentUser.display_name || currentUser.email) : "Tài khoản";
  if (!loggedIn) {
    els.accountButtonCredits?.classList.add("hidden");
    if (els.accountButtonCredits) els.accountButtonCredits.textContent = "";
    els.accountButton?.setAttribute("aria-label", "Tài khoản");
    els.accountButton?.setAttribute("title", "Tài khoản");
  }
  els.accountAuthPanel?.classList.toggle("hidden", loggedIn);
  els.accountProfilePanel?.classList.toggle("hidden", !loggedIn);
  els.accountAdminButton?.classList.toggle("hidden", currentUser?.role !== "admin");
  if (!loggedIn) {
    activeAccountTab = "overview";
    return;
  }
  setAccountTab(activeAccountTab);
  const wallet = currentUser.wallet || {};
  const dailyCredit = Number(wallet.daily || 0);
  const dailyLimit = Number(wallet.daily_limit || 100);
  const purchasedCredit = Number(wallet.purchased || 0);
  const totalCredit = Number(wallet.total ?? (dailyCredit + purchasedCredit));
  const accountName = currentUser.display_name || currentUser.email;
  const storage = currentUser.storage || {};
  const storageUsed = Number(storage.used_bytes || 0);
  const storageLimit = Number(storage.limit_bytes || 0);
  const storagePercent = Math.max(0, Math.min(100, Number(storage.used_percent || 0)));
  const inactiveCleanupDays = Number(storage.inactive_cleanup_days || 0);
  const inactiveCleanupText = inactiveCleanupDays > 0 ? ` Tự dọn ảnh Cloud sau ${inactiveCleanupDays} ngày không sử dụng app.` : "";
  if (els.accountButtonCredits) {
    const balancedToday = remainingDailyImageCount(dailyCredit, BALANCED_1K_COST);
    els.accountButtonCredits.textContent = `${credit(dailyCredit)}cr hôm nay · ≈${credit(balancedToday)} ảnh`;
    els.accountButtonCredits.classList.remove("hidden");
  }
  els.accountButton?.setAttribute("aria-label", `${accountName} - còn ${credit(totalCredit)} credit - lưu trữ Cloud ${bytes(storageUsed)} trên ${bytes(storageLimit)}`);
  els.accountButton?.setAttribute("title", `${accountName} - hôm nay ${credit(dailyCredit)} / ${credit(dailyLimit)} - đã mua ${credit(purchasedCredit)} credit - lưu trữ Cloud ${bytes(storageUsed)} / ${bytes(storageLimit)}`);
  if (els.accountProfileName) els.accountProfileName.textContent = currentUser.display_name || "Chưa đặt tên";
  if (els.accountProfileEmail) els.accountProfileEmail.textContent = currentUser.email;
  if (els.accountProfilePhone) els.accountProfilePhone.textContent = currentUser.phone;
  if (els.accountProfilePlan) els.accountProfilePlan.textContent = currentUser.plan_id || "free";
  if (els.accountProfileNameInput) els.accountProfileNameInput.value = currentUser.display_name || "";
  if (els.accountProfilePhoneInput) els.accountProfilePhoneInput.value = currentUser.phone || "";
  if (els.accountDailyCredits) els.accountDailyCredits.textContent = `${credit(dailyCredit)} / ${credit(dailyLimit)}`;
  if (els.accountPurchasedCredits) els.accountPurchasedCredits.textContent = credit(purchasedCredit);
  if (els.accountStorageUsage) els.accountStorageUsage.textContent = `${bytes(storageUsed)} / ${bytes(storageLimit)}`;
  if (els.accountStorageBar) els.accountStorageBar.style.width = `${storagePercent}%`;
  els.accountStoragePanel?.classList.toggle("is-warning", Boolean(storage.warning));
  els.accountStoragePanel?.classList.toggle("is-near-full", Boolean(storage.near_full));
  if (els.accountStorageHint) {
    els.accountStorageHint.textContent = storage.near_full
      ? `Sắp đầy. Hệ thống sẽ tự xóa ảnh cũ trong lịch sử để giữ Cloud nhẹ.${inactiveCleanupText}`
      : `Ảnh trên Cloud là bản lưu tạm theo gói. Hãy tải ảnh quan trọng về máy.${inactiveCleanupText}`;
  }
  const lowCredit = dailyCredit < 20 || totalCredit < 20;
  els.accountCreditNotice?.classList.toggle("hidden", !lowCredit);
  if (els.accountCreditNotice) els.accountCreditNotice.textContent = lowCredit ? `Credit hôm nay sắp hết. Nạp thêm để tiếp tục tạo ảnh ngay, credit mua không hết hạn.` : "";
  updateCreditCostHint();
  void loadPackages();
  void loadAccountCommerce();
}

async function api<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, { credentials: "same-origin", ...init });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "Không thể thực hiện thao tác");
  return data as T;
}

async function refreshAccount(): Promise<void> {
  try {
    currentUser = (await api<{ user: any }>("/api/account/me")).user;
    render();
    refreshTasksAfterAccountReady();
  } catch {
    currentUser = null;
    render();
  }
}

async function submitJson(url: string, payload: any): Promise<void> {
  const data = await api<{ user: any }>(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
  currentUser = data.user;
  render();
  refreshTasksAfterAccountReady();
  setMessage("Đã hoàn tất", "ok");
}

async function patchProfile(payload: any): Promise<void> {
  const data = await api<{ user: any }>("/api/account/profile", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
  currentUser = data.user;
  render();
  setMessage("Đã lưu hồ sơ", "ok");
}

async function cleanupStorage(): Promise<void> {
  if (!currentUser) return;
  try {
    const data = await api<{ storage: any }>("/api/account/storage/cleanup", { method: "POST" });
    currentUser = { ...currentUser, storage: data.storage };
    render();
    const cleanup = data.storage.cleanup || {};
    setMessage(`Đã dọn ${bytes(cleanup.freed_bytes || 0)} ảnh cũ trên Cloud`, "ok");
  } catch (error) {
    setMessage(error instanceof Error ? error.message : "Không dọn được dung lượng", "error");
  }
}

async function loadPackages(): Promise<void> {
  if (!els.creditPackageList || !currentUser) return;
  try {
    const [data, policy] = await Promise.all([api<{ packages: any[] }>("/api/credit-packages"), api<{ policy: any }>("/api/credit-policy")]);
    cachedCreditPolicy = policy.policy;
    cachedCreditPackages = data.packages || [];
    updateCreditCostHint();
    if (els.creditPolicyList) {
      els.creditPolicyList.innerHTML = `<span>100 credit miễn phí mỗi ngày · khoảng 30 ảnh Nhanh hoặc 20 ảnh Cân bằng 1K</span><span>Credit mua dùng sau credit miễn phí và không hết hạn</span>`;
    }
    els.creditPackageList.innerHTML = cachedCreditPackages.map((item) => {
      return `<article class="credit-package-card ${item.popular ? "popular" : ""}"><div><strong>${escapeHtml(item.name)}${item.popular ? ` <em>Phổ biến nhất</em>` : ""}</strong><span>${credit(item.credits)} credit · ${money(item.price_per_credit)}/credit</span><small>${packageEstimateSummary(item)}</small></div><b>${money(item.price_vnd)}</b><button class="run-button text-sm" type="button" data-buy-package="${escapeHtml(item.id)}">Nạp</button></article>`;
    }).join("");
    (els.creditPackageList.querySelectorAll("[data-buy-package]") as NodeListOf<HTMLButtonElement>).forEach((button: HTMLButtonElement) => button.addEventListener("click", () => showPaymentConfirmation(button.dataset.buyPackage || "")));
  } catch (error) {
    if (els.paymentOrderStatus) els.paymentOrderStatus.textContent = error instanceof Error ? error.message : "Không tải được gói credit";
  }
}

function packageById(packageId: string): any | null {
  return cachedCreditPackages.find((item) => String(item.id) === String(packageId)) || null;
}

function showPaymentConfirmation(packageId: string): void {
  const item = packageById(packageId);
  if (!item || !els.paymentOrderStatus) return;
  clearPendingOrderPoll();
  els.paymentOrderStatus.innerHTML = `
    <div class="payment-confirm-card">
      <strong>Xác nhận nạp ${escapeHtml(item.name)}</strong>
      <span>${credit(item.credits)} credit · ${money(item.price_vnd)}</span>
      <small>Credit mua không hết hạn. Sau khi chuyển khoản thành công, hệ thống tự cộng credit và báo về Telegram cho admin.</small>
      <div class="payment-confirm-actions">
        <button class="run-button text-sm" type="button" data-confirm-buy-package="${escapeHtml(item.id)}">Xác nhận mua</button>
        <button class="ghost-button text-sm" type="button" data-cancel-payment-order>Hủy</button>
      </div>
    </div>
  `;
  (els.paymentOrderStatus.querySelector("[data-confirm-buy-package]") as HTMLButtonElement | null)?.addEventListener("click", () => void createOrder(packageId));
  (els.paymentOrderStatus.querySelector("[data-cancel-payment-order]") as HTMLButtonElement | null)?.addEventListener("click", () => {
    clearPendingOrderPoll();
    if (els.paymentOrderStatus) els.paymentOrderStatus.textContent = "";
  });
}

async function createOrder(packageId: string): Promise<void> {
  clearPendingOrderPoll();
  try {
    if (els.paymentOrderStatus) els.paymentOrderStatus.textContent = "Đang tạo mã QR thanh toán...";
    const data = await api<{ order: any; message: string }>("/api/account/orders", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ package_id: packageId }) });
    const order = data.order;
    renderPaymentOrder(order);
    startPaymentStatusPolling(order.order_code);
  } catch (error) {
    if (els.paymentOrderStatus) els.paymentOrderStatus.textContent = error instanceof Error ? error.message : "Không tạo được đơn";
  }
}

function renderPaymentOrder(order: any): void {
  if (!els.paymentOrderStatus) return;
  const code = escapeHtml(order.order_code || "");
  const amount = money(order.amount_vnd);
  const qrUrl = String(order.qr_url || "");
  const qrMarkup = qrUrl
    ? `<img class="payment-qr-image" src="${escapeHtml(qrUrl)}" alt="QR thanh toán ${code}" loading="eager"><div class="payment-qr-actions"><a class="ghost-button text-sm" href="${escapeHtml(qrUrl)}" download="agentra-${code}.png" target="_blank" rel="noreferrer">Tải QR</a><a class="ghost-button text-sm" href="${escapeHtml(qrUrl)}" target="_blank" rel="noreferrer">Mở QR</a></div>`
    : `<div class="payment-qr-missing">Admin chưa cấu hình mẫu QR SePay.</div>`;
  els.paymentOrderStatus.innerHTML = `
    <div class="payment-order-card" data-payment-order-code="${code}">
      <div class="payment-order-main">
        <strong>Quét QR để nạp credit</strong>
        <span>Đơn ${code} · ${amount}</span>
        <small>Chuyển khoản đúng số tiền và nội dung để hệ thống tự cộng credit.</small>
      </div>
      <div class="payment-transfer-row">
        <span>Nội dung CK</span>
        <b>${code}</b>
        <button class="ghost-button text-sm" type="button" data-copy-payment-code="${code}">Copy</button>
      </div>
      ${qrMarkup}
      <div class="payment-order-waiting" aria-live="polite">Đang chờ thanh toán...</div>
    </div>
  `;
  (els.paymentOrderStatus.querySelector("[data-copy-payment-code]") as HTMLButtonElement | null)?.addEventListener("click", async (event: Event) => {
    const value = (event.currentTarget as HTMLButtonElement).dataset.copyPaymentCode || "";
    try {
      await navigator.clipboard.writeText(value);
      setMessage("Đã copy nội dung chuyển khoản", "ok");
    } catch {
      setMessage("Không copy được, hãy sao chép thủ công mã đơn", "error");
    }
  });
}

function commissionScheduleMarkup(rates: number[]): string {
  const items = (Array.isArray(rates) && rates.length ? rates : [30, 10, 10, 10]).slice(0, 12);
  return `<div class="affiliate-commission-schedule" aria-label="Lịch hoa hồng theo lần nạp">${items.map((rate, index) => `<span>Lần nạp ${index + 1} · <b>${credit(rate)}%</b></span>`).join("")}</div>`;
}

function commissionScheduleCount(rates: number[]): number {
  return (Array.isArray(rates) && rates.length ? rates : [30, 10, 10, 10]).slice(0, 12).length;
}

function startPaymentStatusPolling(orderCode: string): void {
  const normalizedCode = String(orderCode || "").trim().toUpperCase();
  if (!normalizedCode) return;
  pendingOrderCode = normalizedCode;
  const check = async () => {
    if (!pendingOrderCode) return;
    try {
      const data = await api<{ orders: any[] }>("/api/account/orders");
      const order = (data.orders || []).find((item) => String(item.order_code || "").toUpperCase() === pendingOrderCode);
      if (!order || order.status !== "paid") return;
      clearPendingOrderPoll();
      await refreshAccount();
      if (els.paymentOrderStatus) {
        els.paymentOrderStatus.innerHTML = `<div class="payment-success-card"><strong>Đã nạp credit thành công</strong><span>Đơn ${escapeHtml(order.order_code)} đã thanh toán. Credit đã được cộng vào tài khoản.</span></div>`;
      }
      setMessage("Đã mua gói thành công, credit đã được cộng.", "ok");
      await loadAccountCommerce();
    } catch (error) {
      console.warn(error);
    }
  };
  window.setTimeout(check, 2500);
  pendingOrderPollId = window.setInterval(check, 4000);
}

function renderAffiliatePromo(affiliatePayload: any): void {
  if (!els.accountAffiliatePromo) return;
  const settings = affiliatePayload?.settings || {};
  const eligibility = affiliatePayload?.eligibility || {};
  const requiredPlanName = String(eligibility.required_plan_name || settings.required_plan_name || "Cơ bản");
  const rates = affiliatePayload?.affiliate?.commission_rates || settings.commission_rates || [30, 10, 10, 10];
  const bonus = Number(settings.signup_bonus_credits || 100);
  if (affiliatePayload?.affiliate) {
    els.accountAffiliatePromo.innerHTML = `
      <div>
        <strong>Affiliate đã sẵn sàng</strong>
        <span>Mã giới thiệu của bạn đang hoạt động. Theo dõi thành viên, doanh thu và hoa hồng trong tab Affiliate.</span>
      </div>
      <button class="ghost-button text-sm" type="button" data-account-tab-target="affiliate">Xem Affiliate</button>
    `;
    return;
  }
  if (eligibility.eligible) {
    els.accountAffiliatePromo.innerHTML = `
      <div>
        <strong>Bạn đã đủ điều kiện mở Affiliate</strong>
        <span>Hệ thống tự tạo mã riêng. Người nhập mã khi đăng ký nhận ${credit(bonus)} credit trả phí. Khi họ nạp credit, bạn nhận hoa hồng theo ${credit(rates.length)} lần nạp đầu.</span>
        ${commissionScheduleMarkup(rates)}
      </div>
      <button class="run-button text-sm" type="button" data-account-tab-target="affiliate">Mở Affiliate</button>
    `;
    return;
  }
  els.accountAffiliatePromo.innerHTML = `
    <div>
      <strong>Kiếm thêm thu nhập với Affiliate</strong>
      <span>Nâng cấp từ gói ${escapeHtml(requiredPlanName)} để mở mã giới thiệu riêng, nhận hoa hồng khi người được mời nạp credit.</span>
    </div>
    <button class="run-button text-sm" type="button" data-account-tab-target="credits">Nâng cấp để mở</button>
  `;
}

function renderLockedAffiliatePanel(affiliatePayload: any): void {
  if (!els.accountAffiliatePanel) return;
  const settings = affiliatePayload?.settings || {};
  const eligibility = affiliatePayload?.eligibility || {};
  const requiredPlanName = String(eligibility.required_plan_name || settings.required_plan_name || "Cơ bản");
  const rates = settings.commission_rates || [30, 10, 10, 10];
  const bonus = Number(settings.signup_bonus_credits || 100);
  const commissionCount = commissionScheduleCount(rates);
  els.accountAffiliatePanel.innerHTML = `
    <div class="affiliate-locked-panel">
      <div class="affiliate-locked-copy">
        <strong>Mở khóa Affiliate từ gói ${escapeHtml(requiredPlanName)}</strong>
        <span>Chia sẻ AGENTRA IMAGE cho bạn bè, nhóm thiết kế hoặc khách hàng. Người đăng ký bằng mã của bạn nhận ${credit(bonus)} credit trả phí. Khi họ nạp credit, bạn nhận hoa hồng theo ${credit(commissionCount)} lần nạp đầu:</span>
        ${commissionScheduleMarkup(rates)}
        <small>Chỉ tính theo đơn nạp credit thành công của người đã nhập mã giới thiệu khi đăng ký. Từ lần nạp thứ ${credit(commissionCount + 1)} trở đi không tính hoa hồng.</small>
      </div>
      <div class="affiliate-locked-points">
        <span>Theo dõi đăng ký theo User ID</span>
        <span>Hoa hồng minh bạch theo từng đơn</span>
        <span>Rút tiền khi đủ số dư tối thiểu</span>
      </div>
      <button class="run-button" type="button" data-account-tab-target="credits">Nâng cấp gói ${escapeHtml(requiredPlanName)}</button>
    </div>
  `;
}

async function loadAccountCommerce(): Promise<void> {
  if (!currentUser) return;
  try {
    const [affiliate, orders, withdrawals, ledger] = await Promise.all([
      api<{ affiliate: any | null; eligibility?: any; settings?: any; referrals?: any[]; commissions?: any[]; stats?: any }>("/api/account/affiliate"),
      api<{ orders: any[] }>("/api/account/orders"),
      api<{ withdrawals: any[] }>("/api/account/withdrawals"),
      api<{ ledger: any[] }>("/api/account/ledger"),
    ]);
    renderAffiliatePromo(affiliate);
    const eligibility = affiliate.eligibility || {};
    const settings = affiliate.settings || {};
    const requirement = eligibility.required_plan_name || settings.required_plan_name || "Cơ bản";
    if (els.accountAffiliateRequirement) {
      els.accountAffiliateRequirement.textContent = affiliate.affiliate ? "Dashboard riêng của bạn" : `Chỉ gói ${requirement} trở lên`;
    }
    if (els.accountOrderList) els.accountOrderList.innerHTML = orders.orders.length ? `<h4>Lịch sử đơn hàng</h4>${orders.orders.slice(0, 5).map((item) => `<div class="account-history-row"><span>${escapeHtml(item.order_code)}<small>${escapeHtml(item.status)}</small></span><b>${money(item.amount_vnd)}</b></div>`).join("")}` : "";
    if (!affiliate.affiliate) {
      const rates = settings.commission_rates || [30, 10, 10, 10];
      const bonus = Number(settings.signup_bonus_credits || 100);
      if (!eligibility.eligible) {
        renderLockedAffiliatePanel(affiliate);
      } else if (els.accountAffiliatePanel) {
        els.accountAffiliatePanel.innerHTML = `<div class="affiliate-empty"><strong>Sẵn sàng kiếm hoa hồng</strong><span>Hệ thống sẽ tự tạo mã giới thiệu riêng cho tài khoản của bạn. Người nhập mã lúc đăng ký nhận ${credit(bonus)} credit trả phí. Lịch hoa hồng: ${escapeHtml(rates.map((rate: number, index: number) => `lần ${index + 1}: ${rate}%`).join(" · "))}.</span><button class="run-button" type="button" data-register-affiliate>Đăng ký affiliate</button></div>`;
      }
      els.accountWithdrawalForm?.classList.add("hidden");
    } else {
      const codeValue = String(affiliate.affiliate.code || "");
      const link = referralLink(codeValue);
      const stats = affiliate.stats || {};
      const referrals = affiliate.referrals || [];
      const commissions = affiliate.commissions || [];
      const rates = affiliate.affiliate.commission_rates || affiliate.settings?.commission_rates || [30, 10, 10, 10];
      if (els.accountAffiliatePanel) els.accountAffiliatePanel.innerHTML = `
        <div class="affiliate-dashboard">
          <div class="affiliate-balance">
            <span>Mã: <strong>${escapeHtml(codeValue)}</strong></span>
            <span>Hoa hồng khả dụng: <strong>${money(affiliate.affiliate.available_balance)}</strong></span>
          </div>
          <div class="affiliate-share-row">
            <input class="control" value="${escapeHtml(link)}" readonly aria-label="Link giới thiệu">
            <button class="ghost-button text-sm" type="button" data-copy-affiliate-code="${escapeHtml(codeValue)}">Copy mã</button>
            <button class="run-button text-sm" type="button" data-copy-affiliate-link="${escapeHtml(link)}">Copy link</button>
          </div>
          <div class="affiliate-stats-grid">
            <span><b>${credit(stats.referred_count)}</b><small>Đăng ký</small></span>
            <span><b>${credit(stats.paid_referred_count)}</b><small>Đã nạp</small></span>
            <span><b>${money(stats.referred_revenue_vnd)}</b><small>Doanh thu</small></span>
            <span><b>${money(stats.total_commission_vnd)}</b><small>Hoa hồng</small></span>
          </div>
          <p class="field-hint">Lịch hoa hồng: ${escapeHtml(rates.map((rate: number, index: number) => `lần ${index + 1}: ${rate}%`).join(" · "))}. Chỉ gắn mã lúc người dùng đăng ký tài khoản.</p>
          <div class="account-history-list affiliate-history-list">
            <h4>Thành viên nhập mã</h4>
            ${referrals.length ? referrals.slice(0, 8).map((item: any) => `<div class="account-history-row"><span>User ID ${escapeHtml(shortUserId(item.referred_user_id))}<small>${item.commission_count ? `Đã nạp ${credit(item.commission_count)} lần` : "Chưa nạp"} · ${escapeHtml(dateText(item.created_at))}</small></span><b>${money(item.commission_total)}</b></div>`).join("") : `<p class="field-hint">Chưa có thành viên nhập mã.</p>`}
            <h4>Hoa hồng phát sinh</h4>
            ${commissions.length ? commissions.slice(0, 8).map((item: any) => `<div class="account-history-row"><span>User ID ${escapeHtml(shortUserId(item.referred_user_id))}<small>Lần ${credit(item.commission_index)} · đơn ${escapeHtml(item.order_code)} · ${credit(item.rate_percent)}%</small></span><b>${money(item.amount_vnd)}</b></div>`).join("") : `<p class="field-hint">Chưa có hoa hồng.</p>`}
          </div>
        </div>
      `;
      els.accountWithdrawalForm?.classList.remove("hidden");
    }
    (els.accountAffiliatePanel?.querySelectorAll("[data-register-affiliate]") || []).forEach((button: Element) => (button as HTMLButtonElement).addEventListener("click", async () => { try { await api("/api/account/affiliate", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) }); await loadAccountCommerce(); setAffiliateStatus("Đã đăng ký affiliate.", "ok"); } catch (error) { setAffiliateStatus(error instanceof Error ? error.message : "Không đăng ký được affiliate", "error"); } }));
    els.accountAffiliatePanel?.querySelector("[data-copy-affiliate-code]")?.addEventListener("click", (event: Event) => void copyText((event.currentTarget as HTMLElement).getAttribute("data-copy-affiliate-code") || "", "Đã copy mã giới thiệu"));
    els.accountAffiliatePanel?.querySelector("[data-copy-affiliate-link]")?.addEventListener("click", (event: Event) => void copyText((event.currentTarget as HTMLElement).getAttribute("data-copy-affiliate-link") || "", "Đã copy link giới thiệu"));
    if (els.accountOrderList && withdrawals.withdrawals.length) els.accountOrderList.innerHTML += `<h4>Lịch sử rút tiền</h4>${withdrawals.withdrawals.slice(0, 5).map((item) => `<div class="account-history-row"><span>${escapeHtml(item.bank_name)}<small>${escapeHtml(item.status)}</small></span><b>${money(item.amount)}</b></div>`).join("")}`;
    const ledgerItems = (ledger?.ledger || []).slice(0, 8);
    if (els.accountCreditLedgerList) els.accountCreditLedgerList.innerHTML = ledgerItems.length ? `<h4>Lịch sử credit</h4>${ledgerItems.map((item) => `<div class="account-history-row"><span>${escapeHtml(ledgerLabel(item))}<small>${escapeHtml(item.balance_type)} · ${escapeHtml(item.note || "")}</small></span><b>${Number(item.amount) > 0 ? "+" : ""}${credit(item.amount)}</b></div>`).join("")}` : "";
  } catch (error) {
    setAffiliateStatus(error instanceof Error ? error.message : "Không tải được thông tin affiliate", "error");
  }
}

function ledgerLabel(item: any): string {
  const labels: Record<string, string> = { daily_bonus: "Tặng hằng ngày", expiry: "Hết hạn daily", purchase: "Nạp credit", referral_bonus: "Bonus giới thiệu", usage: "Sử dụng" };
  return labels[String(item.kind || "")] || String(item.kind || "Credit");
}

function shortUserId(value: unknown): string {
  const text = String(value || "");
  return text.length > 12 ? `${text.slice(0, 8)}...${text.slice(-4)}` : text;
}

function dateText(value: unknown): string {
  const date = new Date(String(value || ""));
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("vi-VN");
}

function scheduleReferralStatusCheck(): void {
  if (referralStatusTimerId) window.clearTimeout(referralStatusTimerId);
  const code = String(els.accountRegisterReferral?.value || "").trim().toUpperCase();
  if (!code) {
    setReferralStatus("");
    return;
  }
  setReferralStatus("Đang kiểm tra mã giới thiệu...", "checking");
  referralStatusTimerId = window.setTimeout(() => {
    void checkReferralStatus(code);
  }, 350);
}

async function checkReferralStatus(code: string): Promise<void> {
  const requestId = ++referralStatusRequestId;
  try {
    const data = await api<{ referral: { valid: boolean; bonus_credits?: number } }>(`/api/referrals/${encodeURIComponent(code)}`);
    if (requestId !== referralStatusRequestId) return;
    if (data.referral?.valid) {
      const bonus = credit(data.referral.bonus_credits || 100);
      setReferralStatus(`Mã hợp lệ · bạn nhận +${bonus} credit, tương đương tối đa 30 ảnh Nhanh 1K.`, "valid");
    } else {
      setReferralStatus("Mã giới thiệu không hợp lệ. Bạn vẫn có thể đăng ký nếu bỏ trống mã.", "invalid");
    }
  } catch {
    if (requestId !== referralStatusRequestId) return;
    setReferralStatus("Chưa kiểm tra được mã, bạn vẫn có thể thử đăng ký.", "invalid");
  }
}

function toggleRegisterPasswordVisibility(): void {
  const input = els.accountRegisterPassword as HTMLInputElement | null;
  const button = els.accountRegisterPasswordToggle as HTMLButtonElement | null;
  if (!input || !button) return;
  const visible = input.type === "password";
  input.type = visible ? "text" : "password";
  button.setAttribute("aria-pressed", visible ? "true" : "false");
  button.setAttribute("aria-label", visible ? "Ẩn mật khẩu" : "Hiện mật khẩu");
  button.title = visible ? "Ẩn mật khẩu" : "Hiện mật khẩu";
}

function setAffiliateStatus(message: string, type = ""): void {
  if (!els.accountAffiliateStatus) return;
  els.accountAffiliateStatus.textContent = message;
  els.accountAffiliateStatus.className = `account-status ${type}`.trim();
}

export function initAccountUiFeature(): void {
  Object.assign(getLegacyBridge().methods, {
    refreshAccount,
  });
  els.accountButton?.addEventListener("click", () => { setModalOpen(true); setMessage(""); });
  els.mobileAuthPromptButton?.addEventListener("click", () => { setModalOpen(true); setMessage(""); });
  els.accountProfileTabs?.addEventListener("click", (event: Event) => {
    const button = (event.target as HTMLElement).closest<HTMLElement>("[data-account-tab]");
    if (!button) return;
    setAccountTab(cleanAccountTab(button.dataset.accountTab));
  });
  els.accountProfilePanel?.addEventListener("click", (event: Event) => {
    const button = (event.target as HTMLElement).closest<HTMLElement>("[data-account-tab-target]");
    if (!button) return;
    setAccountTab(cleanAccountTab(button.dataset.accountTabTarget));
  });
  els.dailyCreditBuyButton?.addEventListener("click", openCreditPurchase);
  els.dailyCreditWelcomeClose?.addEventListener("click", closeDailyCreditWelcome);
  els.dailyCreditWelcomeUseButton?.addEventListener("click", useDailyCreditNow);
  els.dailyCreditWelcomeBuyButton?.addEventListener("click", openCreditPurchase);
  [els.quality, els.resolution, els.nInput, els.creditMode].forEach((element: any) => {
    element?.addEventListener("change", updateCreditCostHint);
    element?.addEventListener("input", updateCreditCostHint);
  });
  els.settingsGrid?.addEventListener("click", () => window.setTimeout(updateCreditCostHint, 0));
  els.accountModalClose?.addEventListener("click", () => setModalOpen(false));
  els.accountModal?.addEventListener("click", (event: Event) => { if (event.target === els.accountModal) setModalOpen(false); });
  els.accountRegisterReferral?.addEventListener("input", scheduleReferralStatusCheck);
  els.accountRegisterPasswordToggle?.addEventListener("click", toggleRegisterPasswordVisibility);
  els.accountRegisterForm?.addEventListener("submit", async (event: Event) => { event.preventDefault(); try { await submitJson("/api/account/register", { email: els.accountRegisterEmail?.value, phone: els.accountRegisterPhone?.value, password: els.accountRegisterPassword?.value, display_name: els.accountRegisterName?.value, referral_code: els.accountRegisterReferral?.value }); } catch (error) { setMessage(error instanceof Error ? error.message : "Đăng ký thất bại", "error"); } });
  els.accountLoginForm?.addEventListener("submit", async (event: Event) => {
    event.preventDefault();
    if (loginRequestInFlight) return;
    loginRequestInFlight = true;
    const loginForm = els.accountLoginForm as HTMLFormElement | null;
    const submitButton = loginForm?.querySelector('button[type="submit"]') as HTMLButtonElement | null;
    if (submitButton) submitButton.disabled = true;
    try {
      await submitJson("/api/account/login", { identifier: els.accountLoginIdentifier?.value, password: els.accountLoginPassword?.value });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Đăng nhập thất bại", "error");
    } finally {
      loginRequestInFlight = false;
      if (submitButton) submitButton.disabled = false;
    }
  });
  els.accountProfileForm?.addEventListener("submit", async (event: Event) => { event.preventDefault(); try { await patchProfile({ display_name: els.accountProfileNameInput?.value, phone: els.accountProfilePhoneInput?.value }); } catch (error) { setMessage(error instanceof Error ? error.message : "Không lưu được hồ sơ", "error"); } });
  els.accountPasswordForm?.addEventListener("submit", async (event: Event) => { event.preventDefault(); const form = new FormData(event.currentTarget as HTMLFormElement); try { await api("/api/account/password", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ current_password: form.get("current_password"), new_password: form.get("new_password") }) }); (event.currentTarget as HTMLFormElement).reset(); setMessage("Đã đổi mật khẩu", "ok"); } catch (error) { setMessage(error instanceof Error ? error.message : "Không đổi được mật khẩu", "error"); } });
  els.accountLogoutButton?.addEventListener("click", async () => { await api("/api/account/logout", { method: "POST" }); currentUser = null; render(); setMessage("Đã đăng xuất", "ok"); });
  els.accountStorageCleanupButton?.addEventListener("click", () => void cleanupStorage());
  els.accountWithdrawalForm?.addEventListener("submit", async (event: Event) => { event.preventDefault(); const form = new FormData(event.currentTarget as HTMLFormElement); try { await api("/api/account/withdrawals", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ amount: Number(form.get("amount")), bank_name: form.get("bank_name"), account_number: form.get("account_number"), account_holder: form.get("account_holder") }) }); (event.currentTarget as HTMLFormElement).reset(); await loadAccountCommerce(); setAffiliateStatus("Đã gửi yêu cầu rút tiền, chờ Admin duyệt.", "ok"); } catch (error) { setAffiliateStatus(error instanceof Error ? error.message : "Không gửi được yêu cầu rút", "error"); } });
  const referral = referralCodeFromUrl();
  if (referral && els.accountRegisterReferral) {
    els.accountRegisterReferral.value = referral;
    scheduleReferralStatusCheck();
  }
  void refreshAccount();
}
